import React, { useState } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import { useLocation } from "@reach/router";
import { useLanguageContext } from "../../contexts/language-context";
// Icons
import Icon from "../custom-widgets/icon";
import CookieControlIcon from "../../assets/cookie-control-icon.svg";
import EqualHousingLogo from "../../images/equal-housing-lender-logo.svg";

// Styles
import styles from "./footer.module.scss";

/**
 * @typedef {Object} IconData
 * @property {string} lib - like "fal", "far".
 * @property {string} title - title used for the icon.
 * @property {string} name - name of the icon like "person", "chevron".
 * @property {string} class - classes applied to the icon element.
 */

/**
 * @typedef {Object} LinkData
 * @property {string} id - required id for the link element.
 * @property {string} url - required string url for the link to go.
 * @property {IconData} icon - define if we want a custom icon.
 * @property {boolean} show - boolean value to show or hide the link.
 */

/**
 * This component renders the mobile version content of the footer.
 *
 * @param {Object} props - The object containing props.
 * @param {LinkData[]} props.socialLinks - required array of objects with the links data to show.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const MobileFooter = ({ socialLinks = [] }) => {
  function scrollToTop() {
    if (window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }

  const YEAR = new Date().getFullYear();
  const ROUTING_NUMBER = "325070980";
  const [copyRoutingNumber, setCopyRoutingNumber] = useState("");
  const [showCopyIcon, setShowCopyIcon] = useState(true);
  const isSpanish = useLanguageContext();
  let path = useLocation().pathname;
  // Remove trailing slash (/) from the URL
  path = decodeURI(path).replace(/\/$/, "");
  const showPhoneInfo = path !== "/personal-banking/home-loans" && path !== "/es/banca-personal/prestamos-hipotecarios";

  const handleCopy = (event) => {
    event.preventDefault();
    if (typeof window !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(ROUTING_NUMBER);
      setShowCopyIcon(false);
      setCopyRoutingNumber(
        <span className="text-success text-decoration-none">
          <Icon name="check-circle" lib="far" class="mx-1" />
          <span className="text-sm">{isSpanish ? "Copiado" : "Copied"}</span>
        </span>
      );
      // Reset the copy link text after 5 seconds
      const timeoutId = setTimeout(() => {
        setShowCopyIcon(true);
        setCopyRoutingNumber("");
      }, 5000);
      // Clear timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  };

  const BankInfo = () => (
    <>
      {showPhoneInfo && (
        <li className="mb-3">
          <a href="tel:800-324-9375" class="text-decoration-none" id="footer-mobile-c3-tel-link">
            <Icon lib="fal" name="phone" class="mr-2" fixedWidth="1rem" />
            <span className="text-sm">800-324-9375</span>
          </a>
        </li>
      )}
      <li className="mb-3">
        <a
          className="d-flex text-decoration-none align-items-baseline"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.google.com/maps/place/WaFd+Bank+-+Corporate+Office/@47.610432,-122.3382189,17z/data=!3m1!4b1!4m6!3m5!1s0x54906b3f29e8d447:0xf79dee77d94c038b!8m2!3d47.610432!4d-122.335644!16s%2fg%2f11tn2ndr4p?entry=ttu&g_ep=EgoyMDI0MTAyMi4wIKXMDSoASAFQAw%3D%3D"
          id="footer-mobile-wafd-address-link"
        >
          <Icon lib="fal" name="map-marker-alt" class="mr-2" fixedWidth="1rem" />
          <span className="text-sm">425 Pike Street, Seattle, WA 98101</span>
        </a>
      </li>
      <li className="mb-3">
        <Icon lib="fal" name="money-check-alt" class="mr-2 ml-n1 text-primary" fixedWidth="1rem" />
        <span className="text-primary">
          <span className="text-sm">{isSpanish ? "Número de ruta " : "Routing Number "}</span>
          <a
            id="copy-routing-number-link"
            href="/#"
            rel="noopener noreferrer"
            target="_blank"
            className="text-decoration-none"
            type="button"
            onClick={handleCopy}
          >
            <span className="text-sm">{ROUTING_NUMBER}</span>
            {showCopyIcon && <Icon lib="far" name="copy" class="mx-1 text-primary" />}
          </a>
          {copyRoutingNumber}
        </span>
      </li>
    </>
  );

  return (
    <>
      {isSpanish ? (
        <div id="footer-mobile-spanish" className="d-block d-md-none pb-4 pt-3 border-top">
          {/* BACK TO TOP */}
          <div className="text-center text-center py-1 mb-5 bg-light">
            <Icon lib="fas" name="sort-up" class="text-secondary d-block mx-auto fa-lg" />
            <a
              href="#main-content"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
            >
              Volver arriba
            </a>
          </div>

          {/* SOCIAL ICONS */}
          <p className="d-flex justify-content-center">
            {socialLinks.map((item) =>
              item.show ? (
                <a
                  key={item.id}
                  id={item.id}
                  href={item.url}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label={
                    isSpanish
                      ? `Visita el perfil de ${item.icon.title} de WaFd Bank`
                      : `Visit WaFd Bank's ${item.icon.title} profile`
                  }
                >
                  <span className={`mx-2 ${styles.iconWrapper}`}>
                    {item.icon.name === "xtwitter" ? (
                      <svg
                        role="presentation"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="text-white"
                        style={{ height: "1.5rem", width: "1.5rem" }}
                      >
                        <title>{item.icon.title}</title>
                        <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
                      </svg>
                    ) : (
                      <Icon {...item.icon} />
                    )}
                  </span>
                </a>
              ) : null
            )}
          </p>

          {/* OTHER LINKS */}
          <ul className="container mb-4 list-unstyled text-sm">
            {/* Bank info */}
            <BankInfo />
            <li className="mb-3">
              <Link
                id="footer-personal-banking-link"
                className="d-block text-decoration-none"
                to="/es/banca-personal/entrar"
              >
                <Icon name="user" class="mr-2" fixedWidth="1rem" />
                <span className="text-sm">Iniciar sesión en banca personal</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link id="footer-accessibility-link" className="d-block text-decoration-none" to="/accessibility">
                <Icon name="universal-access" class="mr-2" />
                <span className="text-sm">Accessibility</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link id="footer-terms-of-use-link" className="d-block text-decoration-none" to="/terms-of-use">
                <Icon name="file-alt" class="mr-2 fa-w-16" />
                <span className="text-sm">Terms of Use</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-online-and-mobile-banking-service-agreement-link"
                className="d-block text-decoration-none"
                to="/online-and-mobile-banking-service-agreement"
              >
                <Icon name="file-alt" class="mr-2 fa-w-16" />
                <span className="text-sm">Online Banking Service Agreement</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-ccpa-notice-link"
                className="d-block text-decoration-none"
                to="/privacy-center#ccpa-notice"
              >
                <Icon name="server" class="mr-2 fa-w-16" />
                <span className="text-sm">Notice of Data Collection</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link id="footer-privacy-and-security-link" className="d-block text-decoration-none" to="/privacy-center">
                <Icon name="shield-check" class="mr-2" />
                <span className="text-sm">Privacy Center</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-cookie-control-link"
                className="d-block text-decoration-none"
                to="/privacy-center#cookie-control"
              >
                <Icon name="cookie-bite" class="mr-2" />
                <span className="text-sm">Cookie Control</span>
                <img src={CookieControlIcon} className="ml-1 mb-1" alt="" style={{ width: "31px", height: "14px" }} />
              </Link>
            </li>
          </ul>

          {/* LEGAL */}
          <div className="container">
            <p className="text-muted mb-4 text-sm">
              WaFd Bank es un DBA (Doing Business As) de Washington Federal Bank. Miembro de FDIC.{" "}
              <span className="d-inline-block">
                Prestador de Vivienda Igualitaria{" "}
                <img
                  src={EqualHousingLogo}
                  className="mb-1"
                  alt="Equal Housing Lender logo"
                  style={{ width: "22px", height: "16px" }}
                />
              </span>
            </p>
            <p className="text-muted mx-1 text-sm">
              &copy; {YEAR}, WaFd Bank, Todos los derechos reservados. Compañía NMLSR #410394
            </p>
          </div>
        </div>
      ) : (
        <div id="footer-mobile" className="d-block d-md-none pb-4 pt-3">
          {/* BACK TO TOP */}
          <div className="text-center text-center py-1 mb-4 bg-light">
            <Icon lib="fas" name="sort-up" class="text-secondary d-block mx-auto fa-lg" />
            <a
              href="#main-content"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
            >
              Back to Top
            </a>
          </div>

          {/* SOCIAL ICONS */}
          <p className="d-flex justify-content-center mb-4">
            {socialLinks.map((item) => (
              <a
                key={item.id}
                id={`${item.id}-m`}
                href={item.url}
                rel="noopener noreferrer"
                target="_blank"
                aria-label={
                  isSpanish
                    ? `Visita el perfil de ${item.icon.title} de WaFd Bank`
                    : `Visit WaFd Bank's ${item.icon.title} profile`
                }
              >
                <span className={`mx-2 ${styles.iconWrapper}`}>
                  {item.icon.name === "xtwitter" ? (
                    <svg
                      role="presentation"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      className="text-white"
                      style={{ height: "1.5rem", width: "1.5rem" }}
                    >
                      <title>{item.icon.title}</title>
                      <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
                    </svg>
                  ) : (
                    <Icon {...item.icon} />
                  )}
                </span>
              </a>
            ))}
          </p>

          {/* OTHER LINKS */}
          <ul className="container mb-4 list-unstyled">
            {/* Bank info */}
            <BankInfo />
            <li className="mb-3">
              <Link
                id="footer-personal-banking-link"
                className="d-block text-decoration-none"
                to="/personal-banking/login"
              >
                <Icon name="user" class="mr-2" fixedWidth="1rem" />
                <span className="text-sm">Sign in to Personal Banking</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-treasury-express-link"
                className="d-block text-decoration-none"
                to="https://commercial.washingtonfederal.com/smallbusiness/auth"
              >
                <Icon name="university" class="mr-2" />
                <span className="text-sm">Sign in to WAFD Treasury Express</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-treasury-prime-link"
                className="d-block text-decoration-none"
                to="https://commercial.washingtonfederal.com/"
              >
                <Icon name="university" class="mr-2" />
                <span className="text-sm">Sign in to WAFD Treasury Prime</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link id="footer-accessibility-link" className="d-block text-decoration-none" to="/accessibility">
                <Icon name="universal-access" class="mr-2" />
                <span className="text-sm">Accessibility</span>
              </Link>
            </li>

            <li className="mb-3">
              <Link id="footer-terms-of-use-link" className="d-block text-decoration-none" to="/terms-of-use">
                <Icon name="file-alt" class="mr-2 fa-w-16" />
                <span className="text-sm">Terms of Use</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-online-and-mobile-banking-service-agreement-link"
                className="d-block text-decoration-none"
                to="/online-and-mobile-banking-service-agreement"
              >
                <Icon name="file-alt" class="mr-2 fa-w-16" />
                <span className="text-sm">Online Banking Service Agreement</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-ccpa-notice-link"
                className="d-block text-decoration-none"
                to="/privacy-center#ccpa-notice"
              >
                <Icon name="server" class="mr-2 fa-w-16" />
                <span className="text-sm">Notice of Data Collection</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link id="footer-privacy-and-security-link" className="d-block text-decoration-none" to="/privacy-center">
                <Icon name="shield-check" class="mr-2" />
                <span className="text-sm">Privacy Center</span>
              </Link>
            </li>
            <li className="mb-3">
              <Link
                id="footer-cookie-control-link"
                className="d-block text-decoration-none"
                to="/privacy-center#cookie-control"
              >
                <Icon name="cookie-bite" class="mr-2" />
                <span className="text-sm">Cookie Control</span>
                <img src={CookieControlIcon} className="ml-1 mb-1" alt="" style={{ width: "31px", height: "14px" }} />
              </Link>
            </li>
          </ul>

          {/* LEGAL */}
          <div className="container">
            <p className="text-muted mb-4 text-sm">
              WaFd Bank is a DBA (Doing Business As) of Washington Federal Bank. Member FDIC.{" "}
              <span className="d-inline-block">
                Equal Housing Lender{" "}
                <img
                  src={EqualHousingLogo}
                  className="mb-1"
                  alt="Equal Housing Lender logo"
                  style={{ width: "22px", height: "16px" }}
                />
              </span>
            </p>
            <p className="text-muted mb-4 text-sm">
              &copy; {YEAR}, WaFd Bank, All Rights Reserved. NMLSR Company #410394
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileFooter;

MobileFooter.propTypes = {
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.shape({
        lib: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        class: PropTypes.string.isRequired
      })
    })
  )
};
